import React, { useEffect, useState, useContext } from "react";
import { fetchJson } from "../fetch.js";
import * as Helpers from '../helpers.mjs';
import DenseTables from './DenseTables.js';
import BookPageTabs from './BookPageTabs.js';
import BookPageImage from './BookPageImage.js';
import BookControls from './BookControls.js';
import BookCoverSize from './BookCoverSize.js';
import BookBadges from './BookBadges.js';
import SubRatings from './SubRatings.js';
import BookTitle from './BookTitle.js';
import BookProperties from './BookProperties.js';
import BookRelatedProductsCarousel from './BookRelatedProductsCarousel.js';
import BookPackOriginatorLink from "../components/BookPackOriginatorLink.js";
import BookPackControls from './BookPackControls.js';
import Creators from '../components/Creators.js';
import { useElasticData, ExtraDataContext } from "./DataContext.js";
import * as BookPageHelpers from './helpers.js';
import { currency, isEntitled, isInternal, isAdvancedOrLibraryUser, isAdvancedSchoolsUser } from "../helpers.mjs";
import { SearchContext } from "../components/SearchContext.js";

fetchJson("/assets/json/bindingsJson.json").then(r => {
    window.bindings = r;
});
fetchJson("/assets/json/broadBindingsJson.json").then(r => {
    window.broadbindings = r;
});

const BookPackExtraInfo = ({ source }) => { // ORIGINAL VERSION (idx3)
    var bei = null;

    if (source.book_pack_disclaimer !== null && source.book_pack_disclaimer !== "") {
        bei = <p>{source.book_pack_disclaimer}</p>;
    } else {
        if (Helpers.IsBooksForTopic(source)) {
            bei = <p>BooksForTopics book packs contain all of the titles from the relevant BooksForTopics list as displayed on their <a href="https://www.booksfortopics.com/" target="_blank">website</a>. If a book from the list becomes unavailable, we may substitute it with another similar title. Please note: if you order multiple copies of a book pack, you will receive multiples of the same titles. If you'd like a selection of books from this pack or a different tailored list of books, request a bespoke selection from our <a href="/expert-selections">schools’ team.</a></p>
        } else {
            bei = <p>Our book packs are handpicked by our team of librarians and curriculum experts from the titles in our showroom. This ensures that you receive fresh, high-quality titles in each pack. However, it does mean that the number of books in each pack may vary slightly and we can't tell you which titles will be in a pack before you order. Therefore it's not possible to return individual titles or swap books from your pack once you've received it. If you'd like a tailored list of books, try our <a href="/expert-selections">bespoke selection service.</a></p>
        }
    }

    return <><div className="bookPackExtraInfo">{bei}</div></>;
}

const BookPackDisclaimer = ({ source }) => { // NEW VERSION (dyn_book_packs)
    var bei = null;

    const bookPackDisclaimer = source.disclaimer;
    const isBooksForTopicsPack = (source.origin?.replace(/\W+/g, '').toLowerCase() == "booksfortopics");

    if (bookPackDisclaimer !== "") {
        bei = <p dangerouslySetInnerHTML={{ __html: bookPackDisclaimer }} />;
    } else {
        if (isBooksForTopicsPack) {
            bei = <p>BooksForTopics book packs contain all of the titles from the relevant BooksForTopics list as displayed on their <a href="https://www.booksfortopics.com/" target="_blank">website</a>. If a book from the list becomes unavailable, we may substitute it with another similar title. Please note: if you order multiple copies of a book pack, you will receive multiples of the same titles. If you'd like a selection of books from this pack or a different tailored list of books, request a bespoke selection from our <a href="/expert-selections">schools’ team.</a></p>
        } else {
            bei = <p>Our book packs are handpicked by our team of librarians and curriculum experts from the titles in our showroom. This ensures that you receive fresh, high-quality titles in each pack. However, it does mean that the number of books in each pack may vary slightly and we can't tell you which titles will be in a pack before you order. Therefore it's not possible to return individual titles or swap books from your pack once you've received it. If you'd like a tailored list of books, try our <a href="/expert-selections">bespoke selection service.</a></p>
        }
    }

    return <><div className="bookPackExtraInfo">{bei}</div></>;
}

const BookInfo = ({ extraData }) => {

    // let authorCountryInfo = "";
    const { elasticData, elasticIndex, productData, product } = useElasticData();
    const { bestsellersData, awardsData, internalKeywordsData } = useContext(SearchContext);
    
    const [dense, setDense] = useState(false);

    const _source = elasticData;
    const _annotation = _source.annotation ? _source.annotation.split('\n') : "";

    const _index = elasticIndex;
    const hasBookPackIndex = (_index.includes("dyn_book_packs"));
    const isDynamicBookPack = hasBookPackIndex && _source.max_price != null && _source.max_price != _source.min_price;
    const isBooksForTopicsPack = (_source.origin?.replace(/\W+/g, '').toLowerCase() == "booksfortopics");

    let approxBooksCount = 1;
    if (_source.total_books !== null && _source.total_books?.length > 0) {
        approxBooksCount = _source.total_books;
    } else if (extraData) {
        approxBooksCount = extraData.approxBooks;
    }

    const _id = product;

    const isGuestUser = !Helpers.isEntitled();
    const isLibraryUserActual = Helpers.isLibraryUser;
    const isLibraryUser = () => isAdvancedOrLibraryUser();
    const isSchoolsUser = !isLibraryUser();
    const isLibraryUserAndNotBookPack = (isLibraryUser() && !hasBookPackIndex);
    const isForSchoolBasket = Helpers.isForSchoolBasket;

    useEffect(() => {

        BookPageHelpers.initBookPage();

    }, [extraData]);

    function getAuthorCountryFlag() {
        return (
            <>
                {_source.author_is_irish === true && <img src="/assets/img/flags/ireland_2023.svg" alt="Irish author" title="Author from Ireland" className="authorNationalityFlag" />}
                {_source.author_is_scottish === true && <img src="/assets/img/flags/scotland.svg" alt="Scottish author" title="Scottish author" className="authorNationalityFlag" />}
                {_source.author_is_welsh === true && <img src="/assets/img/flags/wales.svg" alt="Welsh author" title="Welsh author" className="authorNationalityFlag" />}
            </>
        )
    }

    function getFormattedDate(dateString, options = { year: 'numeric', month: 'short', day: 'numeric' }) {
        let date = new Date(dateString);
        return date.toLocaleString("en-GB", options);
    }

    // if (!extraData) {
    //     return <div className="bookInfo">
    //             <h1 className="pageTitle">{_source.title}{_source.subtitle ? `: ${_source.subtitle}` : ``}</h1>
    //             <LoadingSpinner overlay={true} />
    //         </div>
    // }

    const makeAward = (t, ba) => {
        const a = awardsData.find(ad => ad.id == ba);
        if (!a) return null;
        return <><span key={ba}>{t} - {a.name} {a.date}</span><br /></>;
    }

    const awon = _source.awards_won ? _source.awards_won.map((ba, bi) => makeAward("Winner", ba)) : [];
    const ashort = _source.awards_short ? _source.awards_short.map((ba, bi) => makeAward("Shortlisted", ba)) : [];
    const along = _source.awards_long ? _source.awards_long.map((ba, bi) => makeAward("Longlisted", ba)) : [];

    let bookAwardsArray = [...awon, ...ashort, ...along];

    bookAwardsArray = bookAwardsArray.filter(n => n);

    return (
        <ExtraDataContext.Provider value={{
            extraData,
            dense,
            _source,
            _index,
            _id,
            _annotation,
            getFormattedDate,
            bestsellersData,
            isGuestUser,
            isLibraryUser, //isAdvancedOrLibraryUser()
            isLibraryUserActual,
            isSchoolsUser,
            isInternal,
            bookAwardsArray,
            isForSchoolBasket,
            internalKeywordsData
        }}>
            <div className="bookInfo">
                <div className={`columns wrap ${(isLibraryUserAndNotBookPack && !hasBookPackIndex) ? 'lidenseView libs' : ''}`}>
                    <div className={`quarter column bookPadding ${(isLibraryUserAndNotBookPack && !hasBookPackIndex) ? 'dense' : ''}`}>
                        <BookPageImage />
                        <BookBadges />
                        {isLibraryUserAndNotBookPack && !hasBookPackIndex && <BookCoverSize />}
                    </div>
                    {hasBookPackIndex ? (<>
                        <div className="half column headlineDetails">
                            <BookTitle source={_source} />
                            {_source.origin !== null && <div className="authorSub inlineItems vAlignCenter">
                                <BookPackOriginatorLink creator={_source.origin} />
                            </div>}
                            <div className="approxBooksSec">
                                <p className="needMore approxBooks" style={{textDecoration:"unset"}}>
                                    {(!_source.primary_candidates || _source.primary_candidates?.length == 0) && isDynamicBookPack && (approxBooksCount > 1) && <span>This pack contains approximately {approxBooksCount} books.</span>}
                                    {(_source.primary_candidates !== null && _source.primary_candidates?.length > 0) && <span>This pack will contain {_source.primary_candidates.length} books.</span>}
                                </p>
                            </div>
                            <div className="annotPadding" style={{whiteSpace:'pre-line'}}>
                                {_source.description && _source.description !== "" && <div className="bibTag">{_source.description}</div>}
                                {_source.long_product_description && <>
                                    <br/><div className="additionalAnnotation">{_source.long_product_description}</div>
                                </>}
                            </div>
                            <BookPackDisclaimer source={_source} />
                        </div>
                        <div className="quarter column">
                            <BookPackControls />
                        </div>
                    </>) : (<>
                        {isLibraryUserAndNotBookPack && <div id="denseView">
                            <div className="denseHeader">
                                <div className="half column headlineDetails">
                                    <BookTitle source={_source} />
                                    <div className="authorSub inlineItems vAlignCenter">
                                        {getAuthorCountryFlag()}
                                        <Creators source={_source} />
                                    </div>
                                    {_source.schools_only_review && !isGuestUser && isAdvancedSchoolsUser() ? <div className="topReview">{_source.schools_only_review}</div> : _source.evaluation && <div className="topReview">{_source.evaluation}</div>}
                                    <DenseTables />
                                </div>
                                <div className="quarter column">
                                    <BookControls />
                                    <SubRatings />
                                </div>
                            </div>
                        </div>}
                        {(isSchoolsUser) && <>
                            <div className="half column headlineDetails">
                                <BookTitle source={_source} />
                                {(_source.creators?.length > 0) && Helpers.IsBooksForTopic(_source) === false && <div className="authorSub inlineItems vAlignCenter">
                                    {getAuthorCountryFlag()}
                                    <Creators source={_source} />
                                </div>}
                                <BookProperties />
                            </div>
                            <div className="quarter column">
                                <BookControls />
                                <SubRatings />
                            </div>
                        </>}
                    </>)}
                </div>
                {hasBookPackIndex ? (<>
                    {_source.primary_candidates?.length > 0 && 
                        <BookRelatedProductsCarousel carouselId={`booksPackContentsCarousel_${_source.id}`} carouselType="isbnList" carouselTitle="Books in this pack" />
                    }
                    {isBooksForTopicsPack === false && (_source.origin !== null && _source.origin !== "") && 
                        <BookRelatedProductsCarousel carouselId={`relatedBookPacksCarousel_${_source.id}`} carouselType="bookPack" carouselTitle="Other" />
                    }
                </>) : (<>
                    {productData && <BookPageTabs />}
                    {(_source.authors !== null && _source.authors !== "") && 
                        <BookRelatedProductsCarousel carouselId={`authorRelatedProductsCarousel_${_source.id}`} carouselType="auth" carouselTitle="More titles by" />
                    }
                    {(_source.illustrators !== null && _source.illustrators !== "") && 
                        <BookRelatedProductsCarousel carouselId={`illustratorRelatedProductsCarousel_${_source.id}`} carouselType="ill" carouselTitle="Other titles illustrated by" />
                    }
                    {_source.series_display !== "" && 
                        <BookRelatedProductsCarousel carouselId={`seriesRelatedProductsCarousel_${_source.id}`} carouselType="series" carouselTitle="Other books in" />
                    }
                </>)}
            </div>
        </ExtraDataContext.Provider>
    );
}

export default BookInfo;